// src/pages/FAQ.jsx

import React from 'react';
import { Container, Typography, Accordion, AccordionSummary, AccordionDetails, Table, TableBody, TableCell, TableHead, TableRow, Button, Box, Paper, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { styled } from '@mui/system';
import { Link } from 'react-router-dom'; // Assuming you use react-router for navigation

// --- STYLING (Hard-Edge, Minimalist, Modern Tech Theme) ---

const Root = styled('div')({
    backgroundColor: '#FFFFFF',
    color: '#111111',
    minHeight: '100vh',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
});

const StyledContainer = styled(Container)({
    paddingTop: '8rem',
    paddingBottom: '6rem',
});

const HeaderTypography = styled(Typography)({
    fontWeight: 700,
    fontSize: '3rem',
    letterSpacing: '-1px',
    marginBottom: '1rem',
    textAlign: 'center',
});

const SubHeaderTypography = styled(Typography)({
    fontSize: '1.25rem',
    color: '#666666',
    marginBottom: '4rem',
    textAlign: 'center',
    maxWidth: '600px',
    marginLeft: 'auto',
    marginRight: 'auto',
});

const StyledAccordion = styled(Accordion)({
    backgroundColor: 'transparent',
    boxShadow: 'none',
    borderBottom: '1px solid #EAEAEA',
    borderRadius: 0,
    margin: 0,
    '&:before': {
        display: 'none',
    },
    '&.Mui-expanded': {
        margin: 0,
    }
});

const StyledAccordionSummary = styled(AccordionSummary)({
    padding: '24px 0',
    minHeight: 'auto',
    '& .MuiAccordionSummary-content': {
        margin: '0',
    },
    '& .MuiTypography-root': {
        fontWeight: 600,
        fontSize: '1.25rem',
        color: '#111111',
    },
});

const StyledAccordionDetails = styled(AccordionDetails)({
    padding: '0 0 24px 0',
    fontSize: '1rem',
    lineHeight: 1.7,
    color: '#444444',
});

const StyledTable = styled(Table)({
    marginTop: '1.5rem',
    '& th, & td': {
        color: '#111111',
        borderColor: '#EAEAEA',
        padding: '12px 16px',
        fontSize: '0.95rem',
    },
    '& th': {
        fontWeight: 'bold',
        backgroundColor: '#FAFAFA',
    },
});

const HelpCenterBox = styled(Box)({
    textAlign: 'center',
    padding: '3rem 2rem',
    marginTop: '5rem',
    borderRadius: '8px',
    border: '1px solid #EAEAEA',
    backgroundColor: '#FAFAFA',
});

const HelpCenterButton = styled(Button)({
    backgroundColor: '#111111',
    color: '#FFFFFF',
    fontWeight: '600',
    padding: '14px 28px',
    borderRadius: '8px',
    marginTop: '1.5rem',
    textTransform: 'none',
    fontSize: '1rem',
    '&:hover': {
        backgroundColor: '#333333',
    },
});

const FAQ = () => {
    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    // ✅ --- FULL FAQ DATA ---
    const faqData = [
        {
            question: "What is Mars Reel® Academy?",
            answer: "Mars Reel® Academy is an athlete membership that offers year-round, weekly training clinics focused on skill development. We provide various unique experiences including scrimmages, fireside chats, and virtual events to help our community grow.<br/><br/>Currently, we offer <strong>basketball training</strong> for boys and girls (ages 8–16), with plans to expand into other sports soon. Register to be notified when new sports become available!"
        },
        {
            question: "How do I sign up and get the app?",
            answer: "You can start by creating an account on our website. Once registered, you can download our mobile app to find sessions and manage your account. The app is available on both iOS and Android."
        },
        {
            question: "Where are the training sessions held?",
            answer: "To browse available training sessions near you, please complete your registration and download our mobile app. The app uses your location to show you the closest and most relevant events."
        },
        {
            question: "What are the pricing and membership options?",
            answer: "We use a 'credit-based' system. You can choose from a flexible membership or pay as you go. All credits, regardless of how they are acquired, do not expire.",
            table: {
                headers: ["Plan", "Price", "Details"],
                rows: [
                    ["Standard Membership", "$150 / month", "4 credits per month. (Approx. 2 sessions)"],
                    ["Premium Membership", "$300 / month", "8 credits per month. (Approx. 4 sessions)"],
                    ["Pay-As-You-Go", "$40 / credit", "Purchase credits individually as needed."]
                ]
            }
        },
        {
            question: "What are credits and how do they work?",
            answer: "Credits are your key to accessing all experiences in the Academy. Most sessions cost 1-2 credits.",
            table: {
                headers: ["Event Type", "Credits"],
                rows: [
                    ["Training Session", "2"],
                    ["Exclusive In-Person Fireside Chat", "3"],
                    ["Virtual Fireside Chat", "1"],
                    ["Scrimmage with Referee", "4"]
                ],
                footer: `Illustrative Credit Costs (as of ${today})`
            }
        },
        {
            question: "Do I get to try it for free?",
            answer: "Yes! As a new user who completes registration, your account is automatically granted <strong>2 free credits</strong>, which is enough to try a full training session at any time, wherever you are."
        },
        {
            question: "What is the refund policy?",
            answer: "While we typically issue credits to your account for cancellations, we do offer cash refunds in certain circumstances, such as accidental charges. Each request is reviewed individually. Processing a refund or credit typically takes up to 7 days."
        }
    ];

    const [expanded, setExpanded] = React.useState(false);

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    return (
        <Root>
            <StyledContainer maxWidth="md">
                <HeaderTypography variant="h2">
                    Frequently Asked Questions
                </HeaderTypography>
                <SubHeaderTypography>
                    Can't find the answer you're looking for? Reach out to our team in the <a href="/help-center" style={{ color: '#12c099', textDecoration: 'underline' }}>Help Center</a>.
                </SubHeaderTypography>

                <Box>
                    {faqData.map((item, index) => (
                        <StyledAccordion
                            key={index}
                            expanded={expanded === `panel${index}`}
                            onChange={handleChange(`panel${index}`)}
                            disableGutters // Removes Accordion spacing
                        >
                            <StyledAccordionSummary
                                expandIcon={expanded === `panel${index}` ? <RemoveIcon /> : <AddIcon />}
                            >
                                <Typography>{item.question}</Typography>
                            </StyledAccordionSummary>
                            <StyledAccordionDetails>
                                <Typography component="div" dangerouslySetInnerHTML={{ __html: item.answer }} />
                                {item.table && (
                                    <>
                                        {item.table.footer && <Typography variant="caption" display="block" sx={{mt: 1, mb: 1, color: '#666666'}}>{item.table.footer}</Typography>}
                                        <StyledTable size="small">
                                            <TableHead>
                                                <TableRow>
                                                    {item.table.headers.map(header => <TableCell key={header}>{header}</TableCell>)}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {item.table.rows.map((row, rIndex) => (
                                                    <TableRow key={rIndex} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                        {row.map((cell, cIndex) => (
                                                            <TableCell key={cIndex} component={cIndex === 0 ? 'th' : 'td'} scope={cIndex === 0 ? 'row' : undefined}>
                                                                {cIndex === 0 ? <strong>{cell}</strong> : cell}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </StyledTable>
                                    </>
                                )}
                            </StyledAccordionDetails>
                        </StyledAccordion>
                    ))}
                </Box>

                <HelpCenterBox>
                    <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
                        Still have questions?
                    </Typography>
                    <Typography sx={{ color: '#444444', maxWidth: '500px', margin: '0 auto' }}>
                        Our team is here to help. Visit our Help Center for more detailed articles or to get in touch with our support team.
                    </Typography>
                    <HelpCenterButton
                        variant="contained"
                        component={Link}
                        to="/help-center"
                    >
                        Visit Help Center
                    </HelpCenterButton>
                </HelpCenterBox>
            </StyledContainer>
        </Root>
    );
};

export default FAQ;
